/* Gummies use the same cart drawer as the bracelet, with one add request per submit. */
(() => {
  if (window.__hbCart) return;
  window.__hbCart = true;
  const route = path => (window.Shopify?.routes?.root || '/') + path;
  const getDrawer = () => document.querySelector('cart-drawer');

  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        const error = new Error(typeof data.description === 'string' ? data.description : 'Unable to update your bag. Please try again.');
        error.rejected = response.status >= 400 && response.status < 500;
        throw error;
      }
      return data;
    } finally {
      clearTimeout(timeout);
    }
  };

  const setPending = (drawer, pending) => {
    if (!drawer) return;
    drawer.classList.toggle('hb-cart-pending', pending);
    drawer.setAttribute('aria-busy', String(pending));
    drawer.querySelectorAll('.cart-drawer__body,.drawer__footer').forEach(el => { el.inert = pending; });
    drawer.querySelector('[data-hb-cart-status]')?.remove();
    if (!pending) return;
    const panel = document.createElement('div');
    panel.className = 'hb-cart-status';
    panel.dataset.hbCartStatus = '';
    panel.setAttribute('role', 'status');
    panel.setAttribute('aria-live', 'polite');
    panel.textContent = 'Adding to your bag…';
    drawer.querySelector('.drawer__inner')?.append(panel);
  };

  const showError = (form, drawer, message) => {
    let error = form.querySelector('[data-hb-cart-error]');
    if (!error) {
      error = document.createElement('div');
      error.dataset.hbCartError = '';
      error.className = 'hb-cart-error';
      error.setAttribute('role', 'alert');
      form.append(error);
    }
    error.replaceChildren(document.createTextNode(message + ' '));
    const link = document.createElement('a');
    link.href = route('cart');
    link.textContent = 'View your bag';
    error.append(link);
    error.hidden = false;
    if (drawer?.classList.contains('active')) {
      const notice = error.cloneNode(true);
      notice.classList.add('hb-cart-status', 'hb-cart-status--error');
      notice.dataset.hbCartStatus = '';
      drawer.querySelector('.drawer__inner')?.append(notice);
    }
  };

  const prepareItems = async (form, autoItems, gummiesView, gummiesVariantIds) => {
    const data = new FormData(form);
    // Keep Kaching's line properties and selling plan when normalizing a single variant.
    if (data.get('id') && !data.has('items[0][id]')) {
      data.set('items[0][id]', data.get('id'));
      data.set('items[0][quantity]', data.get('quantity') || '1');
      data.delete('id');
      data.delete('quantity');
      if (data.get('selling_plan')) data.set('items[0][selling_plan]', data.get('selling_plan'));
      data.delete('selling_plan');
      [...data.keys()].filter(key => key.startsWith('properties[')).forEach(key => {
        data.set('items[0][properties][' + key.slice(11, -1) + ']', data.get(key));
        data.delete(key);
      });
    }
    if (!data.get('items[0][id]')) throw new Error('Please choose an available product option.');
    // Keep hard/soft gift eligibility attached to the real gummies lines only.
    if (gummiesView === 'hard' || gummiesView === 'soft') {
      const variantIds = (gummiesVariantIds || []).map(String);
      [...data.entries()].forEach(([key, value]) => {
        const match = /^items\[(\d+)\]\[id\]$/.exec(key);
        if (match && variantIds.includes(String(value))) {
          data.set(`items[${match[1]}][properties][_hmx_gummies_view]`, gummiesView);
          // 22/10/2026 test A/B/C gummies : version de page vue par l'acheteur (posée par layout/theme.liquid).
          if (window.hxAbTag) data.set(`items[${match[1]}][properties][_hx_ab]`, window.hxAbTag);
        }
      });
      // Test A/B/C : un ajout au panier par clic d'achat, compté dans le dashboard (onglet « Test A/B/C gummies »).
      if (window.hxAbSend) window.hxAbSend([{ k: 'cta', s: 'atc', t: Date.now() }]);
    }
    const cart = await request(route('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const handles = (cart.items || []).map(item => item.handle);
    const ids = [...data.entries()].filter(([key]) => /^items\[\d+\]\[id\]$/.test(key)).map(([, value]) => String(value));
    let index = 0;
    while (data.has(`items[${index}][id]`)) index++;
    autoItems.filter(item => item.id).forEach(item => {
      // 02/10 : the customer's refusal is kept in a cart attribute (set by hxg-sub-cart-guard); the drawer toggle could be
      // stale (a gummies-only cart renders it unchecked) and would silently drop the pre-checked protection of a bracelet.
      if (item.handle === 'shipping-protection-from-damage-loss-theft' && (cart.attributes || {})._hmx_ins_optout === '1') return;
      if (!handles.includes(item.handle) && !ids.includes(String(item.id))) {
        while (data.has(`items[${index}][id]`)) index++;
        data.set(`items[${index}][id]`, String(item.id));
        data.set(`items[${index}][quantity]`, '1');
        ids.push(String(item.id));
        index++;
      }
      handles.push(item.handle);
    });
    // In-memory only: the global gift integration can reuse this fresh cart check.
    data.hbPreparedGiftHandles = handles;
    return data;
  };

  const renderCart = (drawer, result) => {
    if (!drawer || typeof drawer.renderContents !== 'function') throw new Error('Cart drawer unavailable');
    if (!result.sections || !Object.values(result.sections).some(html => typeof html === 'string' && html.includes('id="CartDrawer"'))) {
      throw new Error('Cart section unavailable');
    }
    // The native bracelet form clears this flag; otherwise the theme hides every cart row.
    drawer.classList.remove('is-empty');
    drawer.renderContents(result, true);
    // The theme copies the previous toggle state onto the fresh HTML (fixParsedHtml); realign every toggle with the cart lines.
    drawer.querySelectorAll('cart-drawer-upsell[data-toggle="true"]').forEach(upsell => {
      const inCart = !!drawer.querySelector('.cart-item--product-' + upsell.dataset.handle);
      upsell.dataset.selected = inCart ? 'true' : 'false';
      upsell.querySelector('.upsell-toggle-btn')?.setAttribute('aria-pressed', String(inCart));
    });
    // An already-open drawer has no new opening transition to restore its focus trap.
    setTimeout(() => {
      if (!drawer.classList.contains('active')) return;
      const panel = drawer.querySelector('#CartDrawer');
      const close = drawer.querySelector('.drawer__close');
      if (panel && close && typeof window.trapFocus === 'function') window.trapFocus(panel, close);
    }, 0);
    const count = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]')?.textContent;
    if (count != null) document.querySelectorAll('[data-hb-source-header] .hd-count').forEach(el => { el.textContent = count; });
  };

  /* Gummies (02/10/2026, ordre Tarik) : pas de tiroir panier, l'ajout part directement au paiement.
     Toutes les lignes gummies déjà dans le panier sont retirées d'abord : seul le lot choisi arrive au paiement. */
  // Gummies lines of the form data, rebuilt as /cart/add.js items (used to re-add the exact bundle if the cart drifted).
  const formItems = (data, variantIds) => {
    const items = {};
    [...data.entries()].forEach(([key, value]) => {
      const match = /^items\[(\d+)\]\[(id|quantity|selling_plan)\]$/.exec(key) || /^items\[(\d+)\]\[properties\]\[([^\]]+)\]$/.exec(key);
      if (!match) return;
      const item = items[match[1]] || (items[match[1]] = { properties: {} });
      if (/^items\[\d+\]\[properties\]/.test(key)) item.properties[match[2]] = value;
      else item[match[2]] = parseInt(value, 10) || (match[2] === 'quantity' ? 1 : value);
    });
    return Object.values(items).filter(item => variantIds.includes(String(item.id)));
  };

  const skipToCheckout = async (form, autoItems, gummiesView, gummiesVariantIds) => {
    const button = form.querySelector('[name="add"]');
    const label = button?.textContent;
    const variantIds = (gummiesVariantIds || []).map(String);
    const isGummies = item => { const p = item.properties || {}; return !!(p._hxsub || p._hxbundle || p._hxot) || variantIds.includes(String(item.variant_id)); };
    // The cart guard (hxg-sub-cart-guard) stands still while a purchase is on its way to checkout.
    window.__hxBuying = true;
    form.querySelector('[data-hb-cart-error]')?.remove();
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
      button.textContent = 'SECURING YOUR ORDER…';
    }
    let leaving = false;
    try {
      const cart = await request(route('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const stale = {};
      const giftHandles = autoItems.map(item => item.handle);
      (cart.items || []).forEach(item => {
        if (isGummies(item)) stale[item.key] = 0;
        /* 29/10 : l'ebook « 100 Positions » (_hxebook) repart toujours ; le lot cliqué le remet s'il y a droit. */
        else if ((item.properties || {})._hxebook) stale[item.key] = 0;
        /* Le moteur cadeau du thème remonte l'ebook « Chance to Win » à ×3 au chargement : 1 seul au paiement. */
        else if (giftHandles.includes(item.handle) && item.quantity > 1) stale[item.key] = 1;
      });
      if (Object.keys(stale).length) {
        await request(route('cart/update.js'), {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates: stale })
        });
      }
      const data = await prepareItems(form, autoItems, gummiesView, gummiesVariantIds);
      const result = await request(route('cart/add.js'), {
        method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: data
      });
      /* 02/10 (Tarik : « le bundle doit renvoyer uniquement vers le prix affiché ») : before leaving, the cart must hold
         EXACTLY the clicked bundle. If anything drifted (old lines, theme quantity sync), every gummies line goes and the
         bundle is added again, then checked once more. */
      const wanted = formItems(data, variantIds);
      // Same bundle = same quantity per kind of line (subscription / bundle n / one-time bundle n), nothing else.
      const kind = p => (p._hxsub ? 'sub' : (p._hxonce ? 'once' : 'lot') + (p._hxbundle || '?'));
      const tally = list => list.reduce((acc, item) => {
        const k = kind(item.properties || {});
        acc[k] = (acc[k] || 0) + item.quantity;
        return acc;
      }, {});
      const sameTally = (x, y) => Object.keys(x).length === Object.keys(y).length && Object.keys(x).every(k => x[k] === y[k]);
      const want = tally(wanted);
      for (let attempt = 0; attempt < 2 && wanted.length; attempt++) {
        const now = await request(route('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const lines = (now.items || []).filter(isGummies);
        if (sameTally(tally(lines), want)) break;
        const reset = {};
        lines.forEach(item => { reset[item.key] = 0; });
        await request(route('cart/update.js'), {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates: reset })
        });
        await request(route('cart/add.js'), {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: wanted })
        });
      }
      document.dispatchEvent(new CustomEvent('hb:cart:added', { detail: result }));
      leaving = true;
      /* Retour arrière depuis le paiement (cache de page) : le bouton redevient cliquable. */
      window.addEventListener('pageshow', () => {
        window.__hxBuying = false;
        if (!button) return;
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
        button.textContent = label;
      }, { once: true });
      // Test A/B/C gummies : passage direct au checkout après l'ajout = clic vers le checkout (sinon jamais compté).
      if (window.hxAbSend) window.hxAbSend([{ k: 'cta', s: 'checkout', t: Date.now() }]);
      window.location.assign(route('checkout'));
    } catch (error) {
      const message = error.message === 'The operation was aborted.' || error.name === 'AbortError'
        ? 'Your connection is taking too long. Please try again.'
        : error.message;
      showError(form, null, message);
    } finally {
      if (!leaving) window.__hxBuying = false;
      if (button && !leaving) {
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
        button.textContent = label;
      }
    }
  };

  const init = (scope = document) => {
    scope.querySelectorAll('form[data-hb-form]').forEach(form => {
      if (form.dataset.hbDrawer) return;
      // Shopify's variant input is named "id", which shadows HTMLFormElement.id.
      const config = document.getElementById('hb-cart-config-' + form.getAttribute('id'));
      if (!config) return;
      const { autoItems = [], gummiesView = '', gummiesVariantIds = [], skipCart = false } = JSON.parse(config.textContent);
      form.dataset.hbDrawer = '1';
      let busy = false;
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (busy) return;
        busy = true;
        if (skipCart) return skipToCheckout(form, autoItems, gummiesView, gummiesVariantIds).finally(() => { busy = false; });
        const button = form.querySelector('[name="add"]');
        const label = button?.textContent;
        const drawer = getDrawer();
        let submitted = false;
        let added = false;
        form.querySelector('[data-hb-cart-error]')?.remove();
        if (button) {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-busy', 'true');
          button.classList.add('is-loading');
          button.textContent = 'ADDING…';
        }
        try {
          setPending(drawer, true);
          if (!drawer || typeof drawer.open !== 'function') throw new Error('Your bag is still loading. Please try again.');
          drawer.setActiveElement?.(button);
          drawer.open(button);
          // 03/10 (Tarik : « 170 puis 78 ») : with the drawer back, a new gummies bundle replaces the gummies already in
          // the cart (otherwise old + new lots show for a second until the cart guard cleans them).
          if (gummiesView === 'hard' || gummiesView === 'soft') {
            const current = await request(route('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
            const ids = (gummiesVariantIds || []).map(String), stale = {};
            (current.items || []).forEach(item => { const p = item.properties || {}; if (p._hxsub || p._hxbundle || ids.includes(String(item.variant_id))) stale[item.key] = 0; });
            if (Object.keys(stale).length) await request(route('cart/update.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates: stale }) });
          }
          const data = await prepareItems(form, autoItems, gummiesView, gummiesVariantIds);
          const sections = drawer.getSectionsToRender().map(section => section.id);
          data.set('sections', sections.join(','));
          const sectionUrl = new URL(location.pathname, location.origin);
          const view = new URL(location.href).searchParams.get('view');
          if (view === 'soft' || view === 'hematix-gummies-soft') sectionUrl.searchParams.set('view', view);
          data.set('sections_url', sectionUrl.pathname + sectionUrl.search);
          submitted = true;
          const result = await request(route('cart/add.js'), {
            method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: data
          });
          added = true;
          try {
            renderCart(drawer, result);
          } catch {
            // The add already succeeded. Recover the display with a GET, never another POST.
            const url = new URL(location.href);
            url.searchParams.set('sections', sections.join(','));
            const refreshed = await request(url.toString(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
            renderCart(drawer, { ...result, sections: refreshed });
          }
          document.dispatchEvent(new CustomEvent('hb:cart:added', { detail: result }));
        } catch (error) {
          setPending(drawer, false);
          const message = added
            ? 'Your items were added, but the bag could not refresh. Open your bag to view them.'
            : submitted && !error.rejected
              ? 'We could not confirm the update. Check your bag before trying again.'
              : error.message === 'The operation was aborted.' || error.name === 'AbortError'
                ? 'Your connection is taking too long. Please try again.'
                : error.message;
          showError(form, drawer, message);
        } finally {
          // Keep an error notice, but always release the loading state and controls.
          drawer?.classList.remove('hb-cart-pending');
          drawer?.setAttribute('aria-busy', 'false');
          drawer?.querySelectorAll('.cart-drawer__body,.drawer__footer').forEach(el => { el.inert = false; });
          drawer?.querySelectorAll('[data-hb-cart-status]:not(.hb-cart-status--error)').forEach(el => el.remove());
          if (button) {
            button.removeAttribute('aria-disabled');
            button.removeAttribute('aria-busy');
            button.classList.remove('is-loading');
            button.textContent = label;
          }
          busy = false;
        }
      });
    });
  };
  document.addEventListener('click', event => {
    const link = event.target.closest?.('[data-hb-source-header] .hd-icon[href$="/cart"]');
    const drawer = getDrawer();
    if (!link || !drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(link);
  });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => init(), { once: true }) : init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
